/**
 * Holds the current principal's permissions.
 *
 * The snapshot is replaced wholesale when the server sends a new one, never
 * patched. `version` is an etag over the content, so a caller can skip
 * re-fetching cheaply — and because it is content-derived, two principals with
 * identical grants share one.
 */

import { useMemo, type ReactNode } from "react";

import { AccessContext } from "./context";
import { AccessSnapshot, type PermissionsPayload } from "./snapshot";

export interface AccessProviderProps {
  /** The payload from `GET /me/permissions`, or null while loading. */
  permissions: PermissionsPayload | null;
  /** False while the request is in flight. */
  ready: boolean;
  children: ReactNode;
}

export function AccessProvider({ permissions, ready, children }: AccessProviderProps) {
  const value = useMemo(
    () => ({
      snapshot: permissions ? AccessSnapshot.from(permissions) : AccessSnapshot.empty(),
      ready,
    }),
    [permissions, ready],
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}
